import { __decorate, __param, __metadata } from 'tslib';
import { Metadata } from '@tencent/workbench-core';
import { Inject } from '@tencent/ioc';
import { WORKBENCH_SETTINGS } from '../../constants/metadata-keys.js';
import { Service } from '../../decorators/class/service.js';
import { setValue, getValue } from '../../utils/value-utils.js';
import { StateManager } from '../store/state-manager.js';
import { WorkbenchConfigurationState, WorkbenchMountSettings } from './auto-sync-settings.js';
import { IWorkbenchSettings } from './workbench-settings.interface.js';

var WorkbenchSettingsService_1;
/**
 * Workbench 可配置项
 *
 * @public
 */
let WorkbenchSettingsService = WorkbenchSettingsService_1 = class WorkbenchSettingsService {
    constructor(store, container, platform) {
        this.store = store;
        this.map = new Map();
        this.platform = platform;
        this.container = container;
        this.accessibility = {
            dropdownAutoFocus: true,
            menuClickFocus: true,
        };
        this.menu = {
            schema: [],
        };
        this.contextMenu = {
            schema: [],
            disabled: false,
            hasOverlay: true,
            shouldHideMenuBeforeShow: false,
            multiRows: false,
        };
        this.toolbar = {
            disabled: false,
            alignment: platform === 'pc' ? 'left' : 'bottom',
            fixedKeys: undefined,
            pinnedKey: '',
            schemaAnimation: false,
            gridChunkSize: 5,
            panelHeight: 290,
            respectSoftKeyboard: true,
            buttonActiveStyle: 'backgroundColor',
            topOffset: 0,
            buttonTooltipPlacement: 'bottom',
        };
        this.statusbar = { schema: { left: [], right: [] }, disabled: false };
        this.titlebar = { schema: [], disabled: false, height: 40 };
        this.actionButtons = { schema: {}, disabled: false };
        this.mount = { controllers: [], features: [], services: [] };
        this.editorZone = {
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 0,
        };
        this.extensions = [];
        this.namespace = 'workbench';
        this.idle = false;
        this.debugMode = false;
        this.network = {};
    }
    static getMetadata(target) {
        return Metadata.getOwn(WORKBENCH_SETTINGS, target);
    }
    forEach(callbackFn, thisArg) {
        return this.map.forEach(callbackFn, thisArg);
    }
    set(setting, value) {
        if (typeof setting === 'string') {
            setValue(setting, value, this.map);
            if (Object.values(WorkbenchConfigurationState).includes(setting)) {
                this.store.upsert(setting, value);
            }
        }
        else {
            Object.keys(setting).forEach((key) => {
                const descriptor = Object.getOwnPropertyDescriptor(WorkbenchSettingsService_1.prototype, key);
                if (descriptor && !['set', 'map', 'forEach', 'shallow'].includes(key)) {
                    this[key] = setting[key];
                }
                else {
                    this.set(key, setting[key]);
                }
            });
            this.forEach((v, key) => {
                if (['useLogger', 'container'].includes(key)) {
                    // 跳过复杂对象
                    this[key] = v;
                    return;
                }
                this.map.set(key, this.resolve(v));
            });
        }
    }
    get(setting) {
        return getValue(setting, this.map);
    }
    resolve(value) {
        if (typeof value === 'object') {
            Object.keys(value).forEach((key) => {
                value[key] = this.resolve(value[key]);
            });
        }
        return value;
    }
    shallow(key, settings) {
        Object.keys(settings).forEach((k) => {
            this.set(`${key}.${k}`, settings[k]);
        });
    }
    set platform(setting) {
        this.map.set('platform', setting);
    }
    get platform() {
        var _a;
        return (_a = this.map.get('platform')) !== null && _a !== void 0 ? _a : 'pc';
    }
    set accessibility(setting) {
        this.shallow('accessibility', setting);
    }
    get accessibility() {
        return this.map.get('accessibility');
    }
    set container(setting) {
        this.map.set('container', setting);
    }
    get container() {
        return this.map.get('container');
    }
    set menu(settings) {
        this.shallow('menu', settings);
    }
    get menu() {
        return this.map.get('menu');
    }
    set contextMenu(settings) {
        this.shallow('contextMenu', settings);
    }
    get contextMenu() {
        return this.map.get('contextMenu');
    }
    set toolbar(settings) {
        this.shallow('toolbar', settings);
    }
    get toolbar() {
        return this.map.get('toolbar');
    }
    set statusbar(settings) {
        this.shallow('statusbar', settings);
    }
    get statusbar() {
        return this.map.get('statusbar');
    }
    set titlebar(settings) {
        this.shallow('titlebar', settings);
    }
    get titlebar() {
        return this.map.get('titlebar');
    }
    set actionButtons(settings) {
        this.shallow('actionButtons', settings);
    }
    get actionButtons() {
        return this.map.get('actionButtons');
    }
    set useLogger(settings) {
        this.map.set('useLogger', settings);
    }
    get useLogger() {
        return this.map.get('useLogger');
    }
    set mount(settings) {
        this.shallow('mount', settings);
    }
    get mount() {
        return this.map.get('mount');
    }
    set namespace(settings) {
        this.set('namespace', settings);
    }
    get namespace() {
        return this.map.get('namespace');
    }
    set editorZone(settings) {
        this.shallow('editorZone', settings);
    }
    get editorZone() {
        return this.map.get('editorZone');
    }
    set idle(settings) {
        this.map.set('idle', settings);
    }
    get idle() {
        return this.map.get('idle');
    }
    set debugMode(settings) {
        this.set('debugMode', settings);
    }
    get debugMode() {
        return this.map.get('debugMode');
    }
    addMountSettings(settings) {
        const values = Object.values(WorkbenchMountSettings);
        Object.entries(settings).forEach((item) => {
            const key = item[0];
            const value = item[1];
            if (values.includes(key)) {
                this.store.upsert(key, value);
                if (Array.isArray(this.mount[key]) && Array.isArray(value)) {
                    (this.mount[key]).concat(value);
                }
            }
        });
    }
    set extensions(settings) {
        this.map.set('extensions', settings);
    }
    get extensions() {
        return this.map.get('extensions');
    }
    set network(settings) {
        this.map.set('network', settings);
    }
    get network() {
        return this.map.get('network');
    }
};
WorkbenchSettingsService = WorkbenchSettingsService_1 = __decorate([
    Service({
        provide: IWorkbenchSettings,
    }),
    __param(1, Inject('container')),
    __param(2, Inject('platform')),
    __metadata("design:paramtypes", [StateManager,
        HTMLElement, String])
], WorkbenchSettingsService);

export { WorkbenchSettingsService };
//# sourceMappingURL=setting-service.js.map
